import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnDef,
  ExpandedState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDispatch } from 'react-redux';
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineChevronUpDown,
} from 'react-icons/hi2';
import GridItem from '../../../layout/GridComponents/GridItem';
import SearchBar from '../../../ui/inputs/SearchBar';
import ExportButton from '../../../feedback/ExportButton';
import {
  columnWidthStyle,
  getColumnAlignMeta,
} from '../../../tables/tanstack/tanstackUtils';
import {
  customSortingFn,
  fuzzyFilter,
} from '../../../tables/ultraTable/utils/ultratable.utils';
import {
  addComponentToPdfExport,
  removeComponentFromPdfExport,
} from '../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../types/redux/pdfExports/FileExportsStore';
import { cn } from '../../../../utilities/cn';

export type OverviewDetailRenderer<TData> = (row: TData) => React.ReactNode;

export type OverviewTableExportField = {
  key: string;
  label: string;
};

export interface OverviewTableV2Props<TData extends object> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  title: string;
  pdfIdentifier: string;
  positionDate?: string;
  /** CSV/PDF field keys. Defaults to column accessorKeys (excluding `expand`). */
  exportFields?: string[];
  /** Optional labels for export. Defaults from column headers. */
  exportFieldsMap?: OverviewTableExportField[];
  /** Nested / detail panel content. Omit to disable row expand. */
  renderDetail?: OverviewDetailRenderer<TData>;
  /** Stable row id for expand/sort state across refreshes. */
  getRowId?: (originalRow: TData, index: number) => string;
  /** Shown when `data` is empty. */
  emptyMessage?: string;
  /** Shown when data exists but search/filter matches nothing. */
  noMatchesMessage?: string;
  /** Columns hidden on first render. They stay searchable and are left out of exports. */
  initialColumnVisibility?: VisibilityState;
  /** Optional cap on the table body. Default fills the remaining card height. */
  maxHeight?: string;
  /** When true, the row uses demo purple background. */
  getIsDemoRow?: (row: TData) => boolean;
}

const ROW_HEIGHT = 75;

function alignClassName(
  align: 'left' | 'center' | 'right' | undefined,
  options?: { includeText?: boolean },
): string {
  const includeText = options?.includeText ?? true;
  if (align === 'center') {
    return includeText ? 'justify-center text-center' : 'justify-center';
  }
  if (align === 'right') {
    return includeText ? 'justify-end text-right' : 'justify-end';
  }
  return includeText ? 'justify-start text-left' : 'justify-start';
}

function OverviewTableV2<TData extends object>({
  data,
  columns: columnsProp,
  title,
  pdfIdentifier,
  positionDate,
  exportFields,
  exportFieldsMap,
  renderDetail,
  getRowId,
  emptyMessage = 'No results found',
  noMatchesMessage = 'No matches for current search',
  initialColumnVisibility,
  maxHeight,
  getIsDemoRow,
}: OverviewTableV2Props<TData>) {
  const dispatch = useDispatch();

  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const canExpand = Boolean(renderDetail);

  const columns = useMemo(() => {
    if (canExpand) return columnsProp;
    return columnsProp.filter((col) => col.id !== 'expand');
  }, [columnsProp, canExpand]);

  const table = useReactTable({
    data,
    columns,
    getRowId,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    sortingFns: {
      customSortingFn: customSortingFn,
    },
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => canExpand,
    initialState: {
      columnVisibility: initialColumnVisibility,
    },
    state: {
      globalFilter,
      expanded,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
  });

  const { rows } = table.getRowModel();
  const visibleColumns = table.getVisibleLeafColumns();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => ROW_HEIGHT,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
        navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 20,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    rowVirtualizer.measure();
  }, [expanded, rowVirtualizer]);

  const { csvFields, fieldsMap } = useMemo(() => {
    const labelByField = Object.fromEntries(
      visibleColumns
        .filter((column) => column.id !== 'expand')
        .map((column) => {
          const { header } = column.columnDef;
          return [
            column.id,
            typeof header === 'string' ? header : column.id,
          ] as const;
        }),
    );

    const fields =
      exportFields ??
      Object.keys(labelByField).filter(
        (field) => field !== 'reports' && field !== 'index',
      );

    return {
      csvFields: fields,
      fieldsMap:
        exportFieldsMap ??
        fields.map((field) => ({
          key: field,
          label: labelByField[field] ?? field,
        })),
    };
  }, [visibleColumns, exportFields, exportFieldsMap]);

  useEffect(() => {
    const columnByField = new Map(
      visibleColumns.map((column) => [column.id, column.columnDef] as const),
    );

    const tablePdfExportHandler = async () => ({
      startY: 50,
      // Mirror CustomTable: pass cell renderers + pdfRenderType so status
      // columns get coloured PageLinkButton treatment in the PDF.
      columns: fieldsMap.map((field) => {
        const col = columnByField.get(field.key);
        const meta = getColumnAlignMeta(col?.meta);
        return {
          header: field.label,
          dataKey: field.key,
          id: field.key,
          field: field.key,
          render: col?.cell,
          pdfRenderType: meta?.pdfRenderType,
          pdfWidth: meta?.pdfWidth,
          headerStyle: meta?.align
            ? { textAlign: meta.align }
            : undefined,
          cellStyle: meta?.align ? { textAlign: meta.align } : undefined,
        };
      }),
      body: data,
    });

    dispatch(
      addComponentToPdfExport({
        identifier: pdfIdentifier,
        handler: tablePdfExportHandler,
        type: PdfComponentType.TABLE,
        title,
        positionDate,
        dontMoveToNewPage: true,
      }),
    );

    return () => {
      dispatch(removeComponentFromPdfExport(pdfIdentifier));
    };
  }, [
    dispatch,
    pdfIdentifier,
    title,
    positionDate,
    data,
    fieldsMap,
    visibleColumns,
  ]);

  const emptyStateMessage =
    data.length === 0 ? emptyMessage : noMatchesMessage;

  return (
    <GridItem
      card
      xs={12}
      height="100%"
      style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
      cardStyle={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <div className="flex flex-shrink-0 flex-col items-start justify-between gap-3 border-b border-gray-200 px-4 py-2 md:flex-row md:items-center">
        <h2 className="text-xl font-normal text-mainblue">{title}</h2>
        <div className="flex shrink-0 items-center justify-end gap-3">
          <SearchBar
            searchHandler={setGlobalFilter}
            placeholder="Search..."
            debounceTime={300}
            className="w-[180px] max-w-[180px] py-1 text-xs"
          />
          <ExportButton
            exportData={data}
            pdfIdentifier={pdfIdentifier}
            fields={csvFields}
            fieldsMap={fieldsMap}
            selectedPositionDate={positionDate}
            allowPdfExport={true}
            linkToRaptorExport={true}
          />
        </div>
      </div>

      <div
        ref={tableContainerRef}
        className="scrollbar-custom scrollbar-custom-inner relative min-h-0 min-w-0 w-full flex-1 overflow-auto"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <div className="w-max min-w-full">
          <div className="sticky top-0 z-10 flex border-b border-gray-200 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <div key={headerGroup.id} className="flex w-full">
                {headerGroup.headers.map((header) => {
                  const meta = getColumnAlignMeta(header.column.columnDef.meta);
                  const style = columnWidthStyle(meta);
                  const canSort = header.column.getCanSort();
                  return (
                    <div
                      key={header.id}
                      style={style}
                      className={`flex min-h-full flex-1 select-none items-center self-stretch px-3 py-2 hover:bg-gray-50 ${alignClassName(
                        meta?.align,
                      )} ${canSort ? 'cursor-pointer' : ''} ${meta?.width ? 'flex-none' : ''
                        }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        className={`group/header flex items-center gap-1 ${alignClassName(
                          meta?.align,
                          { includeText: false },
                        )}`}
                      >
                        <span className="text-[11px] font-bold uppercase tracking-wide text-mainblue">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </span>
                        {canSort && (
                          <span className="inline-flex shrink-0">
                            {header.column.getIsSorted() === 'asc' ? (
                              <HiChevronUp className="h-3.5 w-3.5 text-mainblue" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <HiChevronDown className="h-3.5 w-3.5 text-mainblue" />
                            ) : (
                              <HiOutlineChevronUpDown className="h-3.5 w-3.5 text-mainblue/50" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {rows.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              {emptyStateMessage}
            </div>
          ) : (
            <div
              className="relative w-full"
              style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
            >
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) return null;
                const rowData = row.original;
                const isDemoRow = getIsDemoRow?.(rowData) ?? false;

                return (
                  <div
                    key={row.id}
                    data-index={virtualRow.index}
                    ref={(node) => {
                      if (node) {
                        try {
                          rowVirtualizer.measureElement(node);
                        } catch {
                          // measureElement can fail during rapid remounts
                        }
                      }
                    }}
                    className="absolute left-0 top-0 w-full"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <div
                      className={cn(
                        'flex w-full border-b border-gray-100',
                        isDemoRow
                          ? 'bg-demo_bg hover:bg-[#e9d5ff]'
                          : 'bg-white hover:bg-gray-50',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = getColumnAlignMeta(
                          cell.column.columnDef.meta,
                        );
                        const style = columnWidthStyle(meta);
                        return (
                          <div
                            key={cell.id}
                            style={style}
                            className={`flex flex-1 items-center px-3 py-2.5 text-[0.8rem] text-[#444444] ${alignClassName(
                              meta?.align,
                            )} ${meta?.width ? 'flex-none' : ''}`}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {row.getIsExpanded() && renderDetail && (
                      <div className="border-b border-gray-200 bg-[#F3F9FF] px-4 py-3">
                        <div className="rounded border border-gray-200 bg-white">
                          {renderDetail(rowData)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </GridItem>
  );
}

export default OverviewTableV2;
